import { Fragment, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Plus, Power, History, Search, Pencil, LogIn, Timer, Trash2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import { DeleteCompanyDialog } from "../components/admin/DeleteCompanyDialog";
import { EditCompanyDialog } from "../components/admin/EditCompanyDialog";
import { DeletionLog } from "../components/admin/DeletionLog";

const ACTION_LABEL = { created: "Creata", activated: "Attivata", suspended: "Sospesa", updated: "Modificata", impersonated: "Accesso admin" };
const PIE_COLORS = ["#2C4A3B", "#6B8E7A", "#D4AF37", "#3E5C7E", "#9A6B4F", "#B08968", "#8B3A3A", "#7E6B9A"];

export default function Admin() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const emptyForm = { name: "", owner_email: "", owner_password: "", is_paid: false, plan: "base", address: "", city: "", cap: "", province: "", vat: "", tax_code: "" };
  const [f, setF] = useState(emptyForm);
  const [expanded, setExpanded] = useState(null);
  const [audit, setAudit] = useState({});
  const [editFor, setEditFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [trialFor, setTrialFor] = useState(null);
  const [trialDays, setTrialDays] = useState("7");
  const [trialDate, setTrialDate] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [deletions, setDeletions] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [planF, setPlanF] = useState("all");
  const [revenue, setRevenue] = useState(null);
  const [revPeriod, setRevPeriod] = useState("all");
  const [timeline, setTimeline] = useState([]);
  const load = () => api.get("/companies").then(({ data }) => setCompanies(data));
  const loadDeletions = () => api.get("/admin/deletion-log").then(({ data }) => setDeletions(data)).catch(() => {});
  useEffect(() => {
    load();
    loadDeletions();
    api.get("/billing/plans").then(({ data }) => { setPlans(data); if (data[0]) setF((p) => ({ ...p, plan: data[0].key })); });
    api.get("/admin/accounts-timeline").then(({ data }) => setTimeline(data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/admin/revenue-summary", { params: { period: revPeriod } }).then(({ data }) => setRevenue(data)).catch(() => {});
  }, [revPeriod]);

  const create = async () => {
    setEmailErr("");
    if (!f.name || !f.owner_email || !f.owner_password) { toast.error("Nome, email e password owner sono obbligatori"); return; }
    try { await api.post("/companies", f); toast.success("Azienda creata"); setF({ ...emptyForm, plan: plans[0]?.key || "base" }); load(); }
    catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const em = detail.find((d) => Array.isArray(d.loc) && d.loc.includes("owner_email"));
        if (em) setEmailErr(`Email owner non valida: ${em.msg}`);
      } else if (typeof detail === "string" && detail.toLowerCase().includes("email")) {
        setEmailErr(detail);
      }
      toast.error(formatApiError(detail));
    }
  };
  const toggle = async (id) => { await api.post(`/companies/${id}/toggle`); load(); if (expanded === id) loadAudit(id); };
  const extendTrial = (id) => { setTrialFor(id); setTrialDays("7"); setTrialDate(""); };
  const doExtendTrial = async () => {
    let body;
    if (trialDate) body = { expires_at: trialDate };
    else { const days = parseInt(trialDays, 10); if (!days) { toast.error("Inserisci giorni (anche negativi) o una data"); return; } body = { days }; }
    try { await api.post(`/companies/${trialFor}/extend-trial`, body); toast.success(trialDate ? `Scadenza impostata al ${trialDate}` : `Scadenza aggiornata di ${body.days} giorni`); setTrialFor(null); load(); api.get("/admin/revenue-summary", { params: { period: revPeriod } }).then(({ data }) => setRevenue(data)).catch(() => {}); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const exportCompanies = async () => {
    try { const { data } = await api.get("/admin/companies.xlsx", { responseType: "blob" }); const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = "aziende_qcost.xlsx"; a.click(); URL.revokeObjectURL(url); }
    catch { toast.error("Errore export"); }
  };
  const downloadBlob = async (url, filename) => {
    try { const { data } = await api.get(url, { responseType: "blob" }); const u = URL.createObjectURL(data); const a = document.createElement("a"); a.href = u; a.download = filename; a.click(); URL.revokeObjectURL(u); }
    catch { toast.error("Errore nel download del report"); }
  };
  const toggleAcq = async (id) => {
    try { const { data } = await api.post(`/companies/${id}/toggle-acquisti`); toast.success(data.acquisti_enabled ? "Modulo Acquisti attivato" : "Modulo Acquisti disattivato"); load(); }
    catch { toast.error("Errore aggiornamento modulo"); }
  };
  const toggleRicette = async (id) => {
    try { const { data } = await api.post(`/companies/${id}/toggle-ricette`); toast.success(data.ricette_enabled ? "Modulo Ricette attivato" : "Modulo Ricette disattivato"); load(); }
    catch { toast.error("Errore aggiornamento modulo"); }
  };
  const enter = async (c) => {
    try { await impersonate(c.id); navigate("/"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const loadAudit = async (id) => { const { data } = await api.get(`/companies/${id}/audit`); setAudit((prev) => ({ ...prev, [id]: data })); };
  const toggleHistory = async (id) => { if (expanded === id) { setExpanded(null); return; } setExpanded(id); if (!audit[id]) await loadAudit(id); };

  const inp = "border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40";
  const filtered = companies.filter((c) => {
    if (q && !c.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (statusF === "trial") { if (!(c.plan === "trial" && c.status === "active")) return false; }
    else if (statusF !== "all" && c.status !== statusF) return false;
    if (planF !== "all") { if (planF === "gratis") { if (c.is_paid) return false; } else if (c.plan !== planF) return false; }
    return true;
  });
  const pieData = revenue ? Object.entries(revenue.by_plan || {}).map(([name, value]) => ({ name, value })) : [];
  return (
    <div className="space-y-8">
      <div><p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Super-admin</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900">Aziende</h1></div>

      <div className="bg-white border border-stone-200 rounded-sm p-6">
        <div className="flex items-center gap-2 mb-5"><Building2 size={18} className="text-brand" /><h3 className="font-serif text-2xl text-stone-900">Crea azienda</h3></div>
        <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-400 mb-2">Anagrafica</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <input data-testid="company-name" placeholder="Nome azienda" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input data-testid="company-address" placeholder="Indirizzo" className={inp} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
          <input data-testid="company-city" placeholder="Comune" className={inp} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
          <input data-testid="company-cap" placeholder="CAP" className={inp} value={f.cap} onChange={(e) => setF({ ...f, cap: e.target.value })} />
          <input data-testid="company-province" placeholder="Provincia" className={inp} value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} />
          <input data-testid="company-vat" placeholder="Partita IVA" className={inp} value={f.vat} onChange={(e) => setF({ ...f, vat: e.target.value })} />
          <input data-testid="company-tax-code" placeholder="Codice fiscale" className={inp} value={f.tax_code} onChange={(e) => setF({ ...f, tax_code: e.target.value })} />
        </div>
        <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-400 mb-2">Account & piano</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <input data-testid="company-owner-email" placeholder="Email owner" className={`${inp} w-full`} value={f.owner_email} onChange={(e) => { setF({ ...f, owner_email: e.target.value }); if (emailErr) setEmailErr(""); }} />
            {emailErr && <p data-testid="owner-email-error" className="text-xs text-alert mt-1">{emailErr}</p>}
          </div>
          <input data-testid="company-owner-password" placeholder="Password owner" className={inp} value={f.owner_password} onChange={(e) => setF({ ...f, owner_password: e.target.value })} />
          <select data-testid="company-plan" className={inp} value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>
            {plans.map((p) => <option key={p.key} value={p.key}>{`${p.name} (${p.max_users})`}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-stone-700"><input data-testid="company-free" type="checkbox" checked={!f.is_paid} onChange={(e) => setF({ ...f, is_paid: !e.target.checked })} /> Gratis (senza pagamento)</label>
          <button data-testid="company-create-btn" onClick={create} className="rounded-full bg-brand text-white py-2 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-brand-hover"><Plus size={16} /> Crea</button>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input data-testid="company-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome azienda…" className={`${inp} w-full pl-9`} />
        </div>
        <select data-testid="filter-status" className={inp} value={statusF} onChange={(e) => setStatusF(e.target.value)}>
          <option value="all">Tutti gli stati</option><option value="active">Attive</option><option value="trial">In prova</option><option value="expired">Scadute</option><option value="suspended">Sospese</option><option value="pending_payment">In attesa pagamento</option>
        </select>
        <select data-testid="filter-plan" className={inp} value={planF} onChange={(e) => setPlanF(e.target.value)}>
          <option value="all">Tutti i piani</option>
          {plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          <option value="gratis">Gratis</option>
        </select>
        <span data-testid="filter-count" className="text-xs text-stone-500">{filtered.length} di {companies.length}</span>
        <button data-testid="export-companies" onClick={exportCompanies} className="text-xs font-semibold rounded-full border border-brand text-brand px-3 py-1.5 hover:bg-brand hover:text-white transition-colors">Esporta Excel</button>
        <button data-testid="revenue-report-xlsx" onClick={() => downloadBlob("/admin/revenue-report.xlsx", "fatturato_qcost.xlsx")} className="text-xs font-semibold rounded-full border border-stone-300 text-stone-600 px-3 py-1.5 hover:bg-stone-100 transition-colors">Fatturato Excel</button>
        <button data-testid="revenue-report-pdf" onClick={() => downloadBlob("/admin/revenue-report.pdf", "fatturato_qcost.pdf")} className="text-xs font-semibold rounded-full border border-stone-300 text-stone-600 px-3 py-1.5 hover:bg-stone-100 transition-colors">Fatturato PDF</button>
      </div>

      {revenue && (
        <div className="grid lg:grid-cols-4 gap-4">
          <div data-testid="revenue-total" className="bg-brand text-white rounded-sm p-6 noise-overlay">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs uppercase tracking-[0.12em] font-bold text-white/70">Totale incassato</p>
              <select data-testid="revenue-period" value={revPeriod} onChange={(e) => setRevPeriod(e.target.value)} className="bg-white/15 text-white text-xs rounded-full px-2 py-1 outline-none cursor-pointer">
                <option className="text-stone-900" value="all">Sempre</option>
                <option className="text-stone-900" value="year">Quest'anno</option>
                <option className="text-stone-900" value="month">Questo mese</option>
              </select>
            </div>
            <p className="font-serif text-4xl font-light mt-2">€ {revenue.total_paid_eur.toFixed(2)}</p>
            <p className="text-xs text-white/60 mt-1">{revenue.paid_count} pagamenti</p>
            {revenue.by_plan && Object.keys(revenue.by_plan).length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/20 space-y-1">
                {Object.entries(revenue.by_plan).map(([k, v]) => <div key={k} className="flex justify-between text-xs text-white/80"><span className="capitalize">{k}</span><span>€ {v.toFixed(2)}</span></div>)}
              </div>
            )}
          </div>
          <div data-testid="expiring-7d" className="bg-white border border-stone-200 rounded-sm p-6">
            <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">In scadenza (7 giorni)</p>
            <p className="font-serif text-4xl font-light text-alert mt-2">{(revenue.expiring_next_7d || []).length}</p>
            <ul className="text-xs text-stone-500 mt-2 max-h-16 overflow-y-auto">
              {(revenue.expiring_next_7d || []).slice(0, 5).map((e) => <li key={`${e.name}-${e.plan_expires_at}`}>{e.name} · {new Date(e.plan_expires_at).toLocaleDateString("it-IT")}</li>)}
            </ul>
          </div>
          <div data-testid="expiring-month" className="bg-white border border-stone-200 rounded-sm p-6">
            <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">In scadenza questo mese</p>
            <p className="font-serif text-4xl font-light text-stone-900 mt-2">{revenue.expiring_this_month.length}</p>
            <ul className="text-xs text-stone-500 mt-2 max-h-16 overflow-y-auto">
              {revenue.expiring_this_month.slice(0, 5).map((e) => <li key={`${e.name}-${e.plan_expires_at}`}>{e.name} · {new Date(e.plan_expires_at).toLocaleDateString("it-IT")}</li>)}
            </ul>
          </div>
          <div className="bg-white border border-stone-200 rounded-sm p-4">
            <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500 mb-1">Andamento account</p>
            <ResponsiveContainer width="100%" height={120}>
              <BarChart data={timeline}>
                <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#78716C" }} /><YAxis hide />
                <Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="nuovi" fill="#2C4A3B" radius={[3, 3, 0, 0]} />
                <Bar dataKey="scadenze" fill="#8B3A3A" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {revenue && pieData.length > 0 && (
        <div data-testid="revenue-pie" className="bg-white border border-stone-200 rounded-sm p-6">
          <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500 mb-3">Ricavi per piano</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name}: €${e.value.toFixed(0)}`}>
                {pieData.map((e, i) => <Cell key={e.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => `€ ${Number(v).toFixed(2)}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500">
            <th className="text-left font-bold py-4 px-6">Azienda</th><th className="text-left py-4 px-6 font-bold">Stato</th>
            <th className="text-left py-4 px-6 font-bold">Piano</th><th className="text-right py-4 px-6 font-bold">Utenti</th>
            <th className="text-left py-4 px-6 font-bold">Ultimo accesso owner</th><th className="py-4 px-6"></th></tr></thead>
          <tbody>
            {filtered.map((c) => (
              <Fragment key={c.id}>
                <tr data-testid={`company-row-${c.id}`} className="border-b border-stone-100">
                  <td className="py-4 px-6 font-semibold text-stone-800">{c.name}{!c.is_paid && <span className="ml-2 text-xs text-brand bg-brand/10 px-2 py-0.5 rounded-full">gratis</span>}
                    {(c.city || c.vat) && <div className="text-xs text-stone-400 font-normal mt-0.5">{[c.city, c.province].filter(Boolean).join(" ")}{c.vat ? ` · P.IVA ${c.vat}` : ""}</div>}
                  </td>
                  <td className="py-4 px-6"><span className={`text-xs font-bold px-2 py-1 rounded-full ${c.status === "active" ? "text-brand bg-brand/10" : "text-alert bg-alert/10"}`}>{c.status}</span></td>
                  <td className="py-4 px-6 text-stone-600">{c.plan || "—"}</td>
                  <td className="py-4 px-6 text-right text-stone-600">{c.user_count}/{c.max_users}</td>
                  <td className="py-4 px-6 text-stone-600" data-testid={`last-login-${c.id}`}>
                    {c.owner_last_login ? <span title={c.owner_email || ""}>{new Date(c.owner_last_login).toLocaleString("it-IT")}</span> : <span className="text-xs text-stone-400">mai</span>}
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {c.status === "active" && c.plan !== "super" && (
                        <button data-testid={`impersonate-${c.id}`} onClick={() => enter(c)} className="text-stone-400 hover:text-brand flex items-center gap-1 text-xs" title="Entra come owner"><LogIn size={15} /> Entra</button>
                      )}
                      <button data-testid={`edit-${c.id}`} onClick={() => setEditFor(c)} className="text-stone-400 hover:text-brand" title="Modifica"><Pencil size={16} /></button>
                      <button data-testid={`history-${c.id}`} onClick={() => toggleHistory(c.id)} className={`hover:text-brand ${expanded === c.id ? "text-brand" : "text-stone-400"}`} title="Storico"><History size={16} /></button>
                      <button data-testid={`toggle-${c.id}`} onClick={() => toggle(c.id)} className="text-stone-400 hover:text-brand" title="Attiva/Sospendi accesso team"><Power size={16} /></button>
                      {c.plan !== "super" && <button data-testid={`extend-${c.id}`} onClick={() => extendTrial(c.id)} className="text-stone-400 hover:text-brand" title="Estendi scadenza +7 giorni"><Timer size={16} /></button>}
                      {c.plan !== "super" && <button data-testid={`acq-${c.id}`} onClick={() => toggleAcq(c.id)} className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.acquisti_enabled ? "text-brand bg-brand/10" : "text-stone-400 bg-stone-100"}`} title="Abilita/Disabilita modulo Acquisti">Acq</button>}
                      {c.plan !== "super" && <button data-testid={`ric-${c.id}`} onClick={() => toggleRicette(c.id)} className={`text-xs font-bold px-2 py-0.5 rounded-full ${(c.ricette_enabled || c.plan === "business") ? "text-brand bg-brand/10" : "text-stone-400 bg-stone-100"}`} title="Abilita/Disabilita modulo Ricette (incluso nel Business)">Ric</button>}
                      {c.plan !== "super" && <button data-testid={`delete-${c.id}`} onClick={() => setDeleteFor(c)} className="text-stone-400 hover:text-alert" title="Elimina azienda"><Trash2 size={16} /></button>}
                    </div>
                  </td>
                </tr>
                {expanded === c.id && (
                  <tr className="bg-stone-50/60"><td colSpan={6} className="px-6 py-4" data-testid={`audit-${c.id}`}>
                    <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500 mb-3">Storico modifiche</p>
                    {(audit[c.id] || []).length === 0 ? (
                      <p className="text-sm text-stone-400">Nessuna modifica registrata.</p>
                    ) : (
                      <ul className="space-y-2">
                        {(audit[c.id] || []).map((a, i) => (
                          <li key={`${a.at}-${i}`} className="flex items-center gap-3 text-sm flex-wrap">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${a.action === "suspended" ? "text-alert bg-alert/10" : "text-brand bg-brand/10"}`}>{ACTION_LABEL[a.action] || a.action}</span>
                            <span className="text-stone-600">{new Date(a.at).toLocaleString("it-IT")}</span>
                            {a.detail && <span className="text-stone-500">· {a.detail}</span>}
                            <span className="text-stone-400">· {a.by_email || "—"}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <DeletionLog deletions={deletions} />

      {editFor && <EditCompanyDialog company={editFor} plans={plans} onClose={() => setEditFor(null)} onSaved={() => { setEditFor(null); load(); }} />}
      {deleteFor && <DeleteCompanyDialog company={deleteFor} onClose={() => setDeleteFor(null)} onDeleted={() => { setDeleteFor(null); load(); loadDeletions(); }} />}
      {trialFor && (
        <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setTrialFor(null)}>
          <div data-testid="extend-trial-dialog" className="bg-white rounded-sm w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-stone-200">
              <h3 className="font-serif text-xl text-stone-900 flex items-center gap-2"><Timer size={18} className="text-brand" /> Estendi scadenza</h3>
              <button onClick={() => setTrialFor(null)} className="text-stone-400 hover:text-stone-800 text-lg leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Giorni (+ estende / − riduce)</label>
                <input data-testid="extend-trial-days" type="number" value={trialDays} onChange={(e) => { setTrialDays(e.target.value); setTrialDate(""); }} onKeyDown={(e) => { if (e.key === "Enter") doExtendTrial(); }} autoFocus
                  className="w-full mt-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Oppure imposta data esatta</label>
                <input data-testid="extend-trial-date" type="date" value={trialDate} onChange={(e) => { setTrialDate(e.target.value); setTrialDays(""); }}
                  className="w-full mt-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              </div>
            </div>
            <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
              <button onClick={() => setTrialFor(null)} className="px-5 py-2.5 text-sm text-stone-600 hover:bg-stone-100 rounded-sm">Annulla</button>
              <button data-testid="extend-trial-confirm" onClick={doExtendTrial} className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-hover transition-colors">Applica</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
